import React from 'react';

import Loading from 'ui/atoms/Loading/Loading';

import TutorUsageChart from './TutorUsageChart/TutorUsageChart';
import TutorUsageStatCard from './TutorUsageStatCard/TutorUsageStatCard';
import useTutorUsageSection from './useTutorUsageSection';

import './_tutor-usage-section.scss';

type TutorUsageSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

/**
 * TutorUsageSection - Displays real-time usage stats and daily interactions chart.
 * Stats and chart are derived from the /stats/use endpoint response, which
 * returns the raw list of conversations per period. The chart can be filtered
 * by conversation mode (STUDY/PRACTICE/REVISION) via the header select.
 */
function TutorUsageSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: Readonly<TutorUsageSectionProps>): JSX.Element {
  const { activityTitle, dailyInteractions, loading, modeOptions, onModeChange, selectedMode, statCards, t } = useTutorUsageSection({
    allCourseIds,
    classValue,
    groupCourseMap,
    timePeriodValue,
  });

  if (loading) {
    return (
      <div className="tutor-usage-section">
        <div className="tutor-usage-section__loading">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="tutor-usage-section">
      <h2 className="tutor-usage-section__title">{t('chatbot:tutor_usage_realtime_title')}</h2>

      <ul className="tutor-usage-section__stats">
        {statCards.map((card) => (
          <li className="tutor-usage-section__stats-item" key={card.key}>
            <TutorUsageStatCard
              hasInfo={card.hasInfo}
              iconType={card.iconType}
              iconVariant={card.iconVariant}
              infoTooltip={card.infoTooltip}
              label={card.label}
              subtitle={card.subtitle}
              trend={card.trend}
              trendText={card.trendText}
              value={card.value}
            />
          </li>
        ))}
      </ul>

      <TutorUsageChart
        dailyInteractions={dailyInteractions}
        description={t('chatbot:tutor_usage_activity_description')}
        interactionsLabel={t('chatbot:tutor_usage_legend_interactions')}
        modeOptions={modeOptions}
        onModeChange={onModeChange}
        selectedMode={selectedMode}
        title={activityTitle}
      />
    </div>
  );
}

export default TutorUsageSection;
